import { useEffect, useState } from "react";
import { UserButton } from "@clerk/react";
import { toast } from "sonner";
import { ExternalLink, Loader2, Save, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/layout/Navbar";
import { CardView, PhoneFrame } from "@/components/card/CardView";
import { ProfileTab, ContactsTab } from "@/components/dashboard/ProfileTab";
import { LinksTab } from "@/components/dashboard/LinksTab";
import { CameraliTab } from "@/components/dashboard/CameraliTab";
import { ThemeTab } from "@/components/dashboard/ThemeTab";
import { QrTab } from "@/components/dashboard/QrTab";
import { StatsTab } from "@/components/dashboard/StatsTab";
import { useApi, errMsg } from "@/lib/api";

const TABS = [["profile", "Profilo"], ["camerali", "Dati Camerali"], ["contacts", "Contatti"], ["links", "Link & Social"], ["theme", "Aspetto"], ["qr", "QR & NFC"], ["stats", "Statistiche"]];

export default function Dashboard() {
  const api = useApi();
  const [card, setCard] = useState(null);
  const [savedSlug, setSavedSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api("get", "/me/card").then((c) => { setCard(c); setSavedSlug(c.slug); }).catch((e) => toast.error(errMsg(e)));
  }, [api]);

  const update = (patch) => setCard((c) => ({ ...c, ...patch }));
  const set = (k) => (v) => update({ [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const c = await api("put", "/me/card", card);
      setCard(c); setSavedSlug(c.slug);
      toast.success("Scheda salvata");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!card) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard">
      <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 md:px-6">
          <Logo />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="button-view-public">
              <a href={`/c/${savedSlug}`} target="_blank" rel="noreferrer"><ExternalLink size={16} className="mr-2" /> Vedi scheda</a>
            </Button>
            <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setShowPreview(!showPreview)} data-testid="button-toggle-preview"><Smartphone size={16} /></Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="button-save-profile">
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Salva
            </Button>
            <UserButton />
          </div>
        </div>
      </header>
      <main className="mx-auto grid max-w-7xl gap-10 px-4 py-10 md:px-6 lg:grid-cols-12">
        <div className={`lg:col-span-7 ${showPreview ? "hidden lg:block" : ""}`}>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">La tua scheda</h1>
          <p className="mt-2 text-slate-500">Modifica i dati e guarda l'anteprima aggiornarsi in tempo reale.</p>
          <Tabs defaultValue="profile" className="mt-8">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-200/60 p-1">
              {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`dashboard-tab-${v}`} className="flex-1">{l}</TabsTrigger>)}
            </TabsList>
            <TabsContent value="profile" className="mt-6"><ProfileTab card={card} set={set} /></TabsContent>
            <TabsContent value="camerali" className="mt-6"><CameraliTab card={card} set={set} /></TabsContent>
            <TabsContent value="contacts" className="mt-6"><ContactsTab card={card} set={set} /></TabsContent>
            <TabsContent value="links" className="mt-6"><LinksTab card={card} update={update} /></TabsContent>
            <TabsContent value="theme" className="mt-6"><ThemeTab card={card} update={update} /></TabsContent>
            <TabsContent value="qr" className="mt-6"><QrTab card={card} set={set} savedSlug={savedSlug} /></TabsContent>
            <TabsContent value="stats" className="mt-6"><StatsTab /></TabsContent>
          </Tabs>
        </div>
        <aside className={`lg:col-span-5 ${showPreview ? "" : "hidden lg:block"}`} data-testid="live-preview">
          <div className="sticky top-24">
            <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Anteprima live</p>
            <PhoneFrame><CardView card={card} onVCard={() => {}} /></PhoneFrame>
          </div>
        </aside>
      </main>
    </div>
  );
}
